import { AuthInput, Button, InputErrorMessage } from "@cynthai/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import isEqual from "react-fast-compare";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  isApiError,
  apiErrorData,
  extractApiExceptionCodeFromData,
} from "@/core/lib/api-errors";
import { addToast } from "@/core/lib/toast";
import type { ResetPasswordSchema } from "@/features/auth/schemas/resetPassword";
import { resetPasswordSchema } from "@/features/auth/schemas/resetPassword";
import { jwtResetPasswordMutation } from "@/openapi/@tanstack/react-query.gen";
import { m } from "@/paraglide/messages";

const verifySchema = z.object({
  code: z.coerce.string().optional(),
  email: z.string().optional(),
});

function ForgotPassword() {
  const searchParams = Route.useSearch();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement[]>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<ResetPasswordSchema>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const resetPassword = useMutation({
    ...jwtResetPasswordMutation(),
    onSuccess: () => {
      void navigate({ to: "/" });
    },
    onError: (err) => {
      if (isApiError(err)) {
        if (
          extractApiExceptionCodeFromData(apiErrorData(err) ?? null) ===
          "TOO_MANY_ATTEMPTS"
        ) {
          // surfaced by the global response interceptor toast
          return;
        }

        if (isEqual(apiErrorData(err), { detail: "" })) {
          void navigate({ to: "/" });
          return;
        } else if (
          isEqual(apiErrorData(err), { detail: "VERIFY_USER_BAD_TOKEN" })
        ) {
          setErrorMessage("Wrong code");
          (inputRef.current ?? []).forEach((i) =>
            i.setCustomValidity("Wrong code"),
          );
          return;
        } else {
          setErrorMessage("Unknwon server error");
          (inputRef.current ?? []).forEach((i) =>
            i.setCustomValidity("Unknown server error"),
          );
        }
      }
    },
  });

  return (
    <>
      <p className="text-text-primary mb-5 text-2xl font-semibold">
        Create new password
      </p>

      <p className="text-text-secondary mb-10 max-w-75 text-sm font-light">
        {m.password_invalidation()}
      </p>

      <form
        className="max-w-75"
        onSubmit={handleSubmit((data) => {
          if (!searchParams.code) {
            addToast({
              title: m.toast_incorrect_url_missing_code(),
              severity: "danger",
            });
            return;
          }

          if (!searchParams.email) {
            addToast({
              title: m.toast_incorrect_url_missing_email(),
              severity: "danger",
            });
            return;
          }

          resetPassword.mutate({
            body: {
              email: searchParams.email,
              token: searchParams.code,
              password: data.password,
            },
          });
        })}
      >
        <AuthInput
          label="New password"
          placeholder="Enter new password"
          hiddable
          {...register("password")}
          errorMessage={errors.password?.message}
        />
        <AuthInput
          label="Confirm password"
          placeholder="Enter new password again"
          hiddable
          {...register("passwordConfirm")}
          errorMessage={errors.passwordConfirm?.message}
        />

        {errorMessage && (
          <InputErrorMessage className="mb-4">{errorMessage}</InputErrorMessage>
        )}

        <Button
          color="primary"
          className="mt-14 mb-4 w-full"
          type="submit"
          isDisabled={resetPassword.isPending}
          isLoading={resetPassword.isPending}
        >
          Reset password
        </Button>
      </form>
    </>
  );
}

export const Route = createFileRoute("/auth/change-password")({
  component: () => <ForgotPassword />,
  validateSearch: (search) => verifySchema.parse(search),
  beforeLoad: ({ context }) => {
    if (context.user) {
      throw redirect({ to: "/" });
    }
  },
});
